import { required, email } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { loginRedirectPath } from "~/layers/base/utils/account/loginRedirectPath";
import { norceErrorMessage } from "~/layers/base/utils/account/norceErrorMessage";

/** Form state, validation and submit for {@link AccountLoginForm}. */
export const useAccountLogin = () => {
	const { login } = useNorcePwa();
	const { login: gtmLogin } = useGtmEvents();
	const { executeRecaptcha } = useRecaptcha();
	const { $toast } = useNuxtApp();
	const router = useRouter();
	const route = useRoute();
	const localePath = useLocalePath();
	const { t, locale } = useI18n();

	const loading = ref(false);

	const formData = ref({
		loginName: "",
		password: "",
		remember: true
	});

	const rules = computed(() => ({
		loginName: { required, email },
		password: { required }
	}));

	const $v = useVuelidate(rules, formData);

	const errorKeys: Record<string, string> = {
		[ErrorStatusMessage.WRONG_CREDENTIALS]: "wrongCredentials",
		[ErrorStatusMessage.CUSTOMER_DEACTIVATED]: "user_deactivated",
		[ErrorStatusMessage.RECAPTCHA_FAILED]: "recaptcha_failed",
		[ErrorStatusMessage.NO_COMPANY_ACCESS]: "account.no_company_access"
	};

	/** Resolves to `true` when the customer is logged in. */
	const submit = async () => {
		$v.value.$touch();

		if (!(await $v.value.$validate())) return false;

		loading.value = true;

		try {
			const recaptchaToken = await executeRecaptcha("login");
			const user = await login({
				...formData.value,
				recaptchaToken,
				localeCode: locale.value
			});

			if (!user) return false;

			$toast.success(t("successfulLogin"));
			gtmLogin?.();
			userStore().setUser(user);
			router.push(
				loginRedirectPath(route.query.redirect) ?? localePath("/account")
			);

			return true;
		} catch (error) {
			const message = norceErrorMessage(error, "Account login error");

			if (isNorcePwaError(error)) {
				$toast.error(t(errorKeys[message] ?? message));
			}

			return false;
		} finally {
			loading.value = false;
		}
	};

	return { formData, $v, loading, submit };
};
